/**
 * TOON parser — the compact indent format our MCP servers answer in.
 *
 * Only what the tool outputs actually use (verified against real answers in
 * test/fixtures): nested objects by indentation, `key: value` scalars,
 * list arrays (`key[N]:` + `- ` items), tabular arrays
 * (`key[N]{a,b,c}:` + N CSV rows), inline arrays (`key[N]: a,b,c`), empty
 * arrays (`key[0]:`), a keyless root array (`[N]:`), quoted strings, numbers,
 * booleans and null.
 *
 * The parser is tolerant on purpose: a line it cannot place becomes a string
 * value rather than an exception, because a map that shows nothing is a worse
 * outcome than a map that shows the eight POIs it could read.
 */

export type ToonValue =
  | string
  | number
  | boolean
  | null
  | ToonValue[]
  | { [key: string]: ToonValue };

interface Line {
  indent: number;
  text: string;
}

const HEADER = /^([A-Za-z_][\w.-]*)?(?:\[(\d+)\])?(?:\{([^}]*)\})?:(?:\s(.*))?$/;

export function parseToon(source: string): ToonValue {
  const lines = source
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0)
    .map<Line>((l) => ({ indent: l.length - l.trimStart().length, text: l.trim() }));
  const state = { lines, pos: 0 };
  if (lines.length === 0) return {};
  const first = lines[0]!;
  // keyless root array: `[N]:`
  const rootArr = /^\[(\d+)\](?:\{([^}]*)\})?:$/.exec(first.text);
  if (rootArr) {
    state.pos = 1;
    return readArray(state, first.indent, Number(rootArr[1]), rootArr[2]);
  }
  return readObject(state, first.indent);
}

function readObject(state: { lines: Line[]; pos: number }, indent: number): { [k: string]: ToonValue } {
  const obj: { [k: string]: ToonValue } = {};
  while (state.pos < state.lines.length) {
    const line = state.lines[state.pos]!;
    if (line.indent < indent) break;
    if (line.indent > indent) {
      // stray deeper line without a header — attach as text to the last key
      state.pos++;
      continue;
    }
    if (line.text.startsWith('- ')) break; // belongs to an enclosing array
    const m = HEADER.exec(line.text);
    if (!m) {
      // `key: value` where value contains ':' — split on first ': '
      const idx = line.text.indexOf(': ');
      if (idx > 0) {
        obj[line.text.slice(0, idx)] = scalar(line.text.slice(idx + 2));
      } else {
        obj[line.text.replace(/:$/, '')] = null;
      }
      state.pos++;
      continue;
    }
    const [, key, count, fields, rest] = m;
    const name = key ?? '';
    state.pos++;
    if (count !== undefined) {
      const n = Number(count);
      if (rest !== undefined && rest.length > 0) {
        obj[name] = splitCsv(rest).map(scalar); // inline array
      } else {
        obj[name] = readArray(state, line.indent, n, fields);
      }
    } else if (rest !== undefined) {
      obj[name] = scalar(rest);
    } else {
      // nested object (or nothing)
      const next = state.lines[state.pos];
      obj[name] = next && next.indent > line.indent ? readObject(state, next.indent) : null;
    }
  }
  return obj;
}

function readArray(
  state: { lines: Line[]; pos: number },
  headerIndent: number,
  count: number,
  fields: string | undefined,
): ToonValue[] {
  const out: ToonValue[] = [];
  if (count === 0) return out;
  if (fields !== undefined) {
    // tabular: N rows of CSV at deeper indent
    const cols = fields.split(',').map((f) => f.trim());
    while (out.length < count && state.pos < state.lines.length) {
      const line = state.lines[state.pos]!;
      // Inside a list item the rows sit at the SAME indent as their header
      // (`- events[1]{…}:` then `false,"…"`), so a row is anything at header
      // depth or deeper that is neither a new key nor a new list item.
      if (line.indent < headerIndent || line.text.startsWith('- ') || HEADER.test(line.text)) break;
      const cells = splitCsv(line.text).map(scalar);
      const row: { [k: string]: ToonValue } = {};
      cols.forEach((c, i) => (row[c] = cells[i] ?? null));
      out.push(row);
      state.pos++;
    }
    return out;
  }
  // list array: `- ` items. A nested list may sit at the SAME indent as its header
  // (`line_course` emits `- courses[N]:` then its items one level shallower than a leg's
  // nodes do) — the tabular reader above already allows this with `<`; a `- ` item at the
  // enclosing array's depth is always shallower still, so `<` never swallows a sibling.
  while (state.pos < state.lines.length) {
    const line = state.lines[state.pos]!;
    if (line.indent < headerIndent) break;
    if (!line.text.startsWith('- ')) break;
    const itemIndent = line.indent + 2;
    const body = line.text.slice(2);
    // rewrite the item's first line as if it stood at itemIndent, then read it
    state.lines[state.pos] = { indent: itemIndent, text: body };
    const m = HEADER.exec(body);
    const isObjectItem = m !== null || body.includes(': ');
    if (isObjectItem) {
      out.push(readObject(state, itemIndent));
    } else {
      out.push(scalar(body));
      state.pos++;
    }
  }
  return out;
}

export function splitCsv(text: string): string[] {
  const cells: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (inQ) {
      if (ch === '\\' && i + 1 < text.length) {
        // an escape stays whole for scalar(): `\"` does not end the cell, `\\` does not escape the quote after it
        cur += ch + text[i + 1];
        i++;
      } else if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQ = false;
          cur += ch; // keep the quote so scalar() can unquote
        }
      } else cur += ch;
    } else if (ch === '"') {
      inQ = true;
      cur += ch;
    } else if (ch === ',') {
      cells.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

export function scalar(raw: string): ToonValue {
  const t = raw.trim();
  if (t === '' || t === 'null' || t === '~') return null;
  if (t === 'true') return true;
  if (t === 'false') return false;
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    // one pass, left to right: `\\` is a backslash (an encoded polyline carries them), so `\\n`
    // is a backslash before an n, never a line break
    return t.slice(1, -1).replace(/\\(.)/g, (_, c: string) => (c === 'n' ? '\n' : c === 'r' ? '\r' : c === 't' ? '\t' : c));
  }
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return t;
}

/** Walk a parsed value and call `fn` on every object node (depth first). */
export function walk(value: ToonValue, fn: (obj: { [k: string]: ToonValue }, path: string[]) => void, path: string[] = []): void {
  if (Array.isArray(value)) {
    value.forEach((v, i) => walk(v, fn, [...path, String(i)]));
  } else if (value !== null && typeof value === 'object') {
    fn(value, path);
    for (const [k, v] of Object.entries(value)) walk(v, fn, [...path, k]);
  }
}

export const num = (v: ToonValue | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : null;

export const str = (v: ToonValue | undefined): string | null => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : null);
